import { useState, useEffect } from "react";
import Footer from "./Footer";
import config from "../config";

const { API_BASE, UPLOADS_URL } = config;

async function fetchJSON(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status} – ${url}`);
  return res.json();
}

/* ── Extract the src URL from a stored <iframe ...> embed snippet */
function extractIframeSrc(raw = "") {
  if (!raw) return null;
  const match = raw.match(/src=["']([^"']+)["']/i);
  if (match) return match[1];
  if (/^https?:\/\//i.test(raw.trim())) return raw.trim();
  return null;
}

/* ── Get tomorrow at 08:00 ── */
function getTomorrow0800() {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(8, 0, 0, 0);

  const pad = (n) => String(n).padStart(2, "0");
  return (
    tomorrow.getFullYear() +
    "-" + pad(tomorrow.getMonth() + 1) +
    "-" + pad(tomorrow.getDate()) +
    "T" + pad(tomorrow.getHours()) +
    ":" + pad(tomorrow.getMinutes())
  );
}

function Breadcrumb() {
  return (
    <nav className="ct-bread" aria-label="Breadcrumb">
      <a href="/" className="ct-bread__link">
        <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true" style={{ marginRight: 4 }}>
          <path d="M8.354 1.146a.5.5 0 0 0-.708 0l-6 6A.5.5 0 0 0 1.5 8v6a.5.5 0 0 0 .5.5h3.5a.5.5 0 0 0 .5-.5v-3h4v3a.5.5 0 0 0 .5.5H14a.5.5 0 0 0 .5-.5V8a.5.5 0 0 0-.146-.354L8.354 1.146z"/>
        </svg>
        Home
      </a>
      <span className="ct-bread__sep" aria-hidden="true">|</span>
      <span className="ct-bread__current">Contact</span>
    </nav>
  );
}

function InfoCard({ icon, label, value, href }) {
  if (!value) return null;
  const content = (
    <>
      <div className="ct-info__icon">{icon}</div>
      <div className="ct-info__body">
        <span className="ct-info__label">{label}</span>
        <span className="ct-info__value">{value}</span>
      </div>
    </>
  );
  return href ? (
    <a href={href} className="ct-info__card ct-info__card--link">{content}</a>
  ) : (
    <div className="ct-info__card">{content}</div>
  );
}

const PhoneIcon = () => (
  <img src={`${UPLOADS_URL}/svg-icons/telephone.svg`} alt="" aria-hidden="true" className="ct-info__svg-icon" />
);

const EmailIcon = () => (
  <img src={`${UPLOADS_URL}/svg-icons/inbox-email.svg`} alt="" aria-hidden="true" className="ct-info__svg-icon" />
);

const PinIcon = () => (
  <img src={`${UPLOADS_URL}/svg-icons/address-book.svg`} alt="" aria-hidden="true" className="ct-info__svg-icon" />
);

function SuccessModal({ message, onClose }) {
  useEffect(() => {
    const handleKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose]);

  return (
    <div className="ct-modal__backdrop" role="dialog" aria-modal="true" aria-labelledby="ct-modal-title" onClick={onClose}>
      <div className="ct-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ct-modal__icon-wrap">
          <img src={`${UPLOADS_URL}/svg-icons/check.svg`} alt="Success" className="ct-modal__check-icon" />
        </div>
        <h3 id="ct-modal-title" className="ct-modal__title">Booking Request Received!</h3>
        <p className="ct-modal__msg">{message}</p>
        <button className="ct-modal__btn" onClick={onClose}>Done</button>
      </div>
    </div>
  );
}

function SupplierLogoItem({ supplier }) {
  const logoUrl = `${UPLOADS_URL}/suppliers/${supplier.logo_filename}`;
  return (
    <div className="ct-ticker__logo-item" aria-label={supplier.name}>
      <img src={logoUrl} alt={supplier.name} className="ct-ticker__logo-img" draggable="false" />
    </div>
  );
}

function LogoTicker({ suppliers }) {
  const items = [...suppliers, ...suppliers, ...suppliers];
  return (
    <div className="ct-ticker__wrap">
      <div className="ct-ticker__track">
        {items.map((s, i) => (
          <SupplierLogoItem key={`${s.id}-${i}`} supplier={s} />
        ))}
      </div>
    </div>
  );
}

const JOB_TYPES = ["New Install", "Repair", "Regas", "Maintenance", "Inspection", "Other"];
const PROPERTY_TYPES = ["Residential", "Commercial", "Industrial", "Dehumidifiers Sales", "Other"];

function ContactForm({ onSuccess }) {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", job_type: "", property_type: "",
    preferred_date: "", service_address: "", subject: "", message: ""
  });

  const [errors, setErrors] = useState({});
  const [isSending, setIsSending] = useState(false);
  const [statusMsg, setStatusMsg] = useState("");

  const minDatetime = getTomorrow0800();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSending(true);
    setErrors({});
    setStatusMsg("");

    const clientErrors = {};
    if (!form.name.trim()) clientErrors.name = "Name is required.";
    if (!form.email.trim()) clientErrors.email = "Email is required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
      clientErrors.email = "Enter a valid email address.";
    if (!form.message.trim()) clientErrors.message = "Please describe what you need.";

    if (Object.keys(clientErrors).length > 0) {
      setErrors(clientErrors);
      setStatusMsg("Please fix the errors highlighted below.");
      setIsSending(false);
      return;
    }

    const nameClean = form.name.trim();
    const jobPart = form.job_type || "Booking";
    const propPart = form.property_type ? ` (${form.property_type})` : "";
    const builtSubject = form.subject.trim() || `${jobPart}${propPart} – ${nameClean}`;

    const payload = {
      name: nameClean,
      email: form.email.trim(),
      phone: form.phone.trim(),
      job_type: form.job_type,
      property_type: form.property_type,
      preferred_date: form.preferred_date,
      service_address: form.service_address.trim(),
      subject: builtSubject,
      message: form.message.trim(),
      website: ""   // Force empty honeypot
    };

    try {
      const res = await fetch(`${API_BASE}/contact-submit.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (res.ok && data.success) {
        const msg = data.email_sent
          ? "Your booking request has been received. We'll be in touch shortly to confirm."
          : "Your booking request has been saved. We'll be in touch shortly to confirm.";

        setForm({
          name: "", email: "", phone: "", job_type: "", property_type: "",
          preferred_date: "", service_address: "", subject: "", message: ""
        });
        onSuccess(msg);
      } else if (data.errors) {
        setErrors(data.errors);
        setStatusMsg("Please fix the errors highlighted below.");
      } else {
        setStatusMsg(data.message || "Something went wrong.");
      }
    } catch (err) {
      console.error("Booking form submit error:", err);
      setStatusMsg("Could not send your request. Please try again later.");
    } finally {
      setIsSending(false);
    }
  };

  return (
    <form className="ct-form" onSubmit={handleSubmit} noValidate>
      {/* Improved Honeypot - Uncontrolled + stronger hiding */}
      <input
        type="text"
        name="website"
        defaultValue=""
        className="ct-form__honeypot"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        style={{ display: 'none' }}
      />

      {/* Rest of the form remains the same */}
      <div className="ct-form__row">
        <div className="ct-form__field">
          <label htmlFor="ct-name">Full Name *</label>
          <input id="ct-name" name="name" type="text" value={form.name} onChange={handleChange}
            className={errors.name ? "ct-form__input--error" : ""} placeholder="John Doe" disabled={isSending} />
          {errors.name && <span className="ct-form__error">{errors.name}</span>}
        </div>
        <div className="ct-form__field">
          <label htmlFor="ct-email">Email Address *</label>
          <input id="ct-email" name="email" type="email" value={form.email} onChange={handleChange}
            className={errors.email ? "ct-form__input--error" : ""} placeholder="john@example.com" disabled={isSending} />
          {errors.email && <span className="ct-form__error">{errors.email}</span>}
        </div>
      </div>

      <div className="ct-form__row">
        <div className="ct-form__field">
          <label htmlFor="ct-phone">Phone Number</label>
          <input id="ct-phone" name="phone" type="tel" value={form.phone} onChange={handleChange}
            placeholder="+27 73 932 6464" disabled={isSending} />
        </div>
        <div className="ct-form__field">
          <label htmlFor="ct-preferred-date">Preferred Date / Time *</label>
          <input
            id="ct-preferred-date"
            name="preferred_date"
            type="datetime-local"
            value={form.preferred_date}
            onChange={handleChange}
            min={minDatetime}
            step="3600"
            className={errors.preferred_date ? "ct-form__input--error" : ""}
            disabled={isSending}
          />
          {errors.preferred_date && <span className="ct-form__error">{errors.preferred_date}</span>}
          <small style={{fontSize:"12px",color:"#666",marginTop:"4px",display:"block"}}>
            Only future dates from tomorrow • 08:00 – 17:00
          </small>
        </div>
      </div>

      <div className="ct-form__row">
        <div className="ct-form__field">
          <label htmlFor="ct-job-type">Service Required</label>
          <select id="ct-job-type" name="job_type" value={form.job_type} onChange={handleChange}
            className={errors.job_type ? "ct-form__input--error" : ""} disabled={isSending}>
            <option value="">Select a service…</option>
            {JOB_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div className="ct-form__field">
          <label htmlFor="ct-property-type">Property Type</label>
          <select id="ct-property-type" name="property_type" value={form.property_type} onChange={handleChange}
            className={errors.property_type ? "ct-form__input--error" : ""} disabled={isSending}>
            <option value="">Select property type…</option>
            {PROPERTY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
      </div>

      <div className="ct-form__field">
        <label htmlFor="ct-service-address">Service Address</label>
        <input id="ct-service-address" name="service_address" type="text"
          value={form.service_address} onChange={handleChange}
          placeholder="Where should our technician attend?" disabled={isSending} />
      </div>

      <div className="ct-form__field">
        <label htmlFor="ct-subject">Subject</label>
        <input id="ct-subject" name="subject" type="text"
          value={form.subject} onChange={handleChange}
          placeholder="Brief summary of your request" disabled={isSending} />
      </div>

      <div className="ct-form__field">
        <label htmlFor="ct-message">Tell Us More *</label>
        <textarea id="ct-message" name="message" rows={6}
          value={form.message} onChange={handleChange}
          className={errors.message ? "ct-form__input--error" : ""}
          placeholder="Describe the unit/system, the issue..." disabled={isSending} />
        {errors.message && <span className="ct-form__error">{errors.message}</span>}
      </div>

      {statusMsg && <div className="ct-form__status ct-form__status--error">{statusMsg}</div>}

      <button type="submit" className="ct-form__submit" disabled={isSending}>
        <img src={`${UPLOADS_URL}/svg-icons/schedule.svg`} alt="" className="ct-form__submit-icon" />
        {isSending ? "Submitting…" : "Request Booking"}
      </button>
    </form>
  );
}

function ContactPage() {
  const [contact, setContact] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successModal, setSuccessModal] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    Promise.all([
      fetchJSON(`${API_BASE}/company-contact.php`),
      fetchJSON(`${API_BASE}/video-suppliers.php`).catch(() => ({ suppliers: [] })),
    ])
      .then(([contactData, vsData]) => {
        if (cancelled) return;
        setContact(contactData || null);
        setSuppliers(vsData?.suppliers || []);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("ContactPage load error:", err);
        setError("Failed to load page.");
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, []);

  if (loading) return null;

  if (error) return (
    <div className="ct-error">
      <h2>Something went wrong</h2>
      <p>We could not load the contact page. Please try again later.</p>
      <a href="/" className="ct-error__link">Back to Home</a>
    </div>
  );

  const mapSrc = extractIframeSrc(contact?.address_iframe_link);
  const heroImage = contact?.image1 || null;

  return (
    <>
      {successModal && (
        <SuccessModal message={successModal} onClose={() => setSuccessModal(null)} />
      )}

      <div className="ct">
        <div className="ct-hero">
          {heroImage && (
            <img src={`${UPLOADS_URL}/company-contact/${heroImage}`} alt="Contact Us" className="ct-hero__img" />
          )}
          <div className="ct-hero__overlay" />
          <div className="ct-hero__content">
            <Breadcrumb />
            <h1 className="ct-hero__title">Contact Us</h1>
          </div>
        </div>

        <div className="ct-body">
          <div className="ct-body__inner">
            <div className="ct-heading-block">
              <div className="ct-content__eyebrow ct-content__eyebrow--center">
                <span className="ct-content__eyebrow-icon" aria-hidden="true">✦</span>
                Book A Service
              </div>
              <h2 className="ct-form-heading ct-form-heading--center">Request Your HVAC Service</h2>
              <p className="ct-form-desc ct-form-desc--center">
                Tell us a bit about the job — installation, repair, regas, or maintenance — and our team will confirm your booking shortly.
              </p>
            </div>

            <div className="ct-body__cols">
              <div className="ct-info-cards">
                <InfoCard icon={<PhoneIcon />} label="Call Us" value={contact?.phone_number}
                  href={contact?.phone_number ? `tel:${contact.phone_number}` : null} />
                <InfoCard icon={<EmailIcon />} label="Email Us" value={contact?.email_address}
                  href={contact?.email_address ? `mailto:${contact.email_address}` : null} />
                <InfoCard icon={<PinIcon />} label="Visit Us" value={contact?.physical_address}
                  href={contact?.physical_address_link || null} />
              </div>

              <div className="ct-form-col">
                <ContactForm onSuccess={(msg) => setSuccessModal(msg)} />
              </div>

              <div className="ct-map-col">
                {mapSrc ? (
                  <iframe src={mapSrc} className="ct-map" style={{ border: 0 }} allowFullScreen="" loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade" title="Our location" />
                ) : (
                  <div className="ct-map ct-map--placeholder">Map unavailable</div>
                )}

                {contact?.physical_address_link && (
                  <a href={contact.physical_address_link} target="_blank" rel="noopener noreferrer" className="ct-map__directions">
                    Get Directions
                    <svg width="13" height="13" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                      <path d="M2 7h10M8 3l4 4-4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>

        {suppliers.length > 0 && (
          <div className="ct-ticker__bar">
            <LogoTicker suppliers={suppliers} />
          </div>
        )}

        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;600;700;800;900&display=swap');

          .ct *, .ct *::before, .ct *::after { box-sizing: border-box; margin: 0; padding: 0; }
          .ct a { text-decoration: none; }
          .ct { font-family: 'Roboto', sans-serif; background: #f5f7fa; }

          .ct-error { text-align: center; padding: 80px 24px; font-family: 'Roboto', sans-serif; color: #424244; }
          .ct-error h2 { font-size: 28px; color: #013d97; margin-bottom: 12px; }
          .ct-error p  { margin-bottom: 24px; }
          .ct-error__link { display: inline-block; padding: 10px 24px; background: #029add; color: #fff; border-radius: 6px; font-weight: 700; }

          /* ── HERO ── */
          .ct-hero { position: relative; height: 260px; overflow: hidden; background: #013d97; }
          .ct-hero__img { width: 100%; height: 100%; object-fit: cover; object-position: center; display: block; }
          .ct-hero__overlay { position: absolute; inset: 0; background: linear-gradient(to right, rgba(1,21,70,.8) 40%, rgba(1,61,151,.45) 100%); }
          .ct-hero__content { position: absolute; inset: 0; display: flex; flex-direction: column; justify-content: flex-end; padding: 32px 40px; max-width: 1280px; margin: 0 auto; left: 0; right: 0; }
          .ct-hero__title { font-size: clamp(28px, 3.5vw, 46px); font-weight: 900; color: #fff; line-height: 1.1; margin-top: 10px; }

          .ct-bread { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
          .ct-bread__link { font-size: 13px; font-weight: 600; color: rgba(255,255,255,.85); display: flex; align-items: center; }
          .ct-bread__link:hover { color: #fff; }
          .ct-bread__sep { color: rgba(255,255,255,.5); font-size: 14px; }
          .ct-bread__current { font-size: 13px; color: rgba(255,255,255,.7); }

          /* ── INFO CARDS ── */
          .ct-info__card {
            display: flex; align-items: center; gap: 16px;
            background: #fff; padding: 22px; border-radius: 8px;
            box-shadow: 0 2px 14px rgba(0,0,0,.06);
            transition: transform .2s, box-shadow .2s;
          }
          .ct-info__card--link:hover { transform: translateY(-3px); box-shadow: 0 8px 24px rgba(0,0,0,.1); }
          .ct-info__icon {
            width: 48px; height: 48px; border-radius: 50%; flex-shrink: 0;
            background: #e8f1fb;
            display: flex; align-items: center; justify-content: center;
          }
          .ct-info__svg-icon { width: 22px; height: 22px; object-fit: contain; }
          .ct-form__submit-icon { width: 18px; height: 18px; object-fit: contain; flex-shrink: 0; }
          .ct-modal__check-icon { width: 40px; height: 40px; object-fit: contain; filter: brightness(0) invert(1); }
          .ct-info__body { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
          .ct-info__label { font-size: 11.5px; font-weight: 700; color: #029add; text-transform: uppercase; letter-spacing: .07em; }
          .ct-info__value { font-size: 14.5px; font-weight: 700; color: #1a1a2e; word-break: break-word; }

          /* ── BODY ── */
          .ct-body { padding: 64px 0 88px; }
          .ct-body__inner { max-width: 1280px; margin: 0 auto; padding: 0 40px; }

          .ct-heading-block { max-width: 640px; margin: 0 auto 48px; text-align: center; }

          .ct-content__eyebrow { display: flex; align-items: center; gap: 8px; font-size: 13px; font-weight: 700; color: #029add; letter-spacing: .08em; text-transform: uppercase; margin-bottom: 14px; }
          .ct-content__eyebrow--center { justify-content: center; }
          .ct-content__eyebrow-icon { font-size: 14px; color: #029add; }
          .ct-form-heading { font-size: clamp(24px, 2.8vw, 34px); font-weight: 900; color: #1a1a2e; line-height: 1.2; margin-bottom: 14px; text-align: center; }
          .ct-form-desc { font-size: 14.5px; color: #5a5f72; line-height: 1.75; margin-bottom: 0; max-width: 480px; text-align: center; margin: 0 auto; }

          .ct-map-col { display: flex; flex-direction: column; gap: 16px; }

          .ct-body__cols {
            display: grid;
            grid-template-columns: 1.1fr 0.9fr;
            grid-template-rows: auto auto;
            grid-template-areas: "form  cards" "form  map";
            gap: 16px 56px;
            align-items: start;
          }
          .ct-info-cards { grid-area: cards; display: flex; flex-direction: column; gap: 16px; }
          .ct-form-col  { grid-area: form; }
          .ct-map-col   { grid-area: map; }

          /* ── FORM ── */
          .ct-form { display: flex; flex-direction: column; gap: 18px; background: #fff; padding: 32px; border-radius: 8px; box-shadow: 0 2px 14px rgba(0,0,0,.06); }
          .ct-form__honeypot { position: absolute; left: -9999px; width: 1px; height: 1px; opacity: 0; }
          .ct-form__row { display: grid; grid-template-columns: 1fr 1fr; gap: 18px; }
          .ct-form__field { display: flex; flex-direction: column; gap: 6px; }
          .ct-form__field label { font-size: 13px; font-weight: 700; color: #1a1a2e; }
          .ct-form__field input, .ct-form__field textarea, .ct-form__field select {
            font-family: 'Roboto', sans-serif; font-size: 14px; color: #1a1a2e;
            padding: 12px 14px; border: 1px solid #dde2ec; border-radius: 5px;
            background: #f9fafc; transition: border-color .2s, background .2s;
            resize: vertical;
          }
          .ct-form__field input:disabled,
          .ct-form__field textarea:disabled,
          .ct-form__field select:disabled { opacity: .6; cursor: not-allowed; }

          .ct-form__field select {
            appearance: none;
            background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 14 14' fill='none'%3E%3Cpath d='M2 4.5l5 5 5-5' stroke='%235a5f72' stroke-width='1.8' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
            background-repeat: no-repeat;
            background-position: right 14px center;
            background-size: 12px;
            padding-right: 36px;
            cursor: pointer;
          }
          .ct-form__field input:focus, .ct-form__field textarea:focus, .ct-form__field select:focus {
            outline: none; border-color: #029add; background: #fff;
          }
          .ct-form__input--error { border-color: #e0473e !important; }
          .ct-form__error { font-size: 12px; color: #e0473e; font-weight: 600; }
          .ct-form__status { padding: 12px 16px; border-radius: 5px; font-size: 13.5px; font-weight: 600; }
          .ct-form__status--error { background: #fdeceb; color: #c43d33; }
          .ct-form__submit {
            display: inline-flex; align-items: center; justify-content: center; gap: 8px;
            background: #013d97; color: #fff; border: none; cursor: pointer;
            font-size: 14px; font-weight: 800; letter-spacing: .04em;
            padding: 14px 28px; border-radius: 3px;
            transition: background .2s, transform .2s;
            align-self: flex-start;
          }
          .ct-form__submit:hover:not(:disabled) { background: #012a6b; transform: translateY(-2px); }
          .ct-form__submit:disabled { opacity: .65; cursor: not-allowed; transform: none; }

          /* ── MAP ── */
          .ct-map { width: 100%; height: 340px; border-radius: 8px; display: block; box-shadow: 0 2px 14px rgba(0,0,0,.08); }
          .ct-map--placeholder { display: flex; align-items: center; justify-content: center; background: #e8ecf3; color: #8890a0; font-size: 14px; }
          .ct-map__directions {
            display: inline-flex; align-items: center; gap: 8px; align-self: flex-start;
            background: #013d97; color: #fff;
            font-size: 13px; font-weight: 800; letter-spacing: .04em;
            padding: 12px 22px; border-radius: 3px;
            transition: background .2s, transform .2s;
          }
          .ct-map__directions:hover { background: #012a6b; transform: translateY(-2px); }

          /* ── SUCCESS MODAL ── */
          .ct-modal__backdrop {
            position: fixed; inset: 0; z-index: 9999;
            background: rgba(1, 21, 70, 0.65);
            display: flex; align-items: center; justify-content: center;
            padding: 24px;
          }
          .ct-modal {
            background: #fff; border-radius: 16px; padding: 44px 36px 36px;
            max-width: 420px; width: 100%; text-align: center;
            box-shadow: 0 30px 80px rgba(0,0,0,.22);
          }
          .ct-modal__icon-wrap {
            width: 72px; height: 72px; border-radius: 50%; background: #1a8a4c;
            display: flex; align-items: center; justify-content: center;
            margin: 0 auto 22px;
          }
          .ct-modal__title { font-size: 22px; font-weight: 900; color: #1a1a2e; margin-bottom: 12px; }
          .ct-modal__msg { font-size: 14.5px; color: #5a5f72; line-height: 1.75; margin-bottom: 28px; }
          .ct-modal__btn {
            background: #e96c29; color: #fff; border: none; padding: 13px 40px; border-radius: 3px;
            font-weight: 800; cursor: pointer; transition: background .2s;
          }
          .ct-modal__btn:hover { background: #c85a1f; }

          /* ── SUPPLIER TICKER BAR ── */
          .ct-ticker__bar { background: #fff; border-top: 1px solid #e8eaf0; box-shadow: 0 -2px 20px rgba(0,0,0,.06); overflow: hidden; }
          .ct-ticker__wrap {
            overflow: hidden; width: 100%; padding: 14px 0;
            -webkit-mask-image: linear-gradient(to right, transparent 0%, #000 8%, #000 92%, transparent 100%);
            mask-image: linear-gradient(to right, transparent 0%, #000 8%, #000 92%, transparent 100%);
          }
          .ct-ticker__track { display: flex; align-items: center; width: max-content; animation: ct-ticker-scroll 38s linear infinite; }
          .ct-ticker__track:hover { animation-play-state: paused; }
          @keyframes ct-ticker-scroll { 0% { transform: translateX(0); } 100% { transform: translateX(-33.333%); } }
          .ct-ticker__logo-item {
            position: relative; display: flex; align-items: center; justify-content: center;
            padding: 10px 36px; border-right: 1px solid #e8eaf0;
            cursor: pointer; flex-shrink: 0; transition: background 0.2s;
          }
          .ct-ticker__logo-item:hover { background: #f7f9ff; }
          .ct-ticker__logo-img {
            height: 36px; width: auto; max-width: 140px; object-fit: contain;
            filter: grayscale(1) opacity(.65); transition: filter 0.25s;
          }
          .ct-ticker__logo-item:hover .ct-ticker__logo-img { filter: grayscale(0) opacity(1); }

          /* ── RESPONSIVE ── */
          @media (max-width: 900px) {
            .ct-body__cols {
              grid-template-columns: 1fr;
              grid-template-areas: "cards" "form" "map";
              gap: 24px;
            }
            .ct-body__inner { padding: 0 24px; }
            .ct-heading-block { padding: 0 4px; }
          }
          @media (max-width: 600px) {
            .ct-hero { height: 200px; }
            .ct-hero__content { padding: 20px 20px; }
            .ct-hero__title { font-size: 28px; }
            .ct-body { padding: 48px 0 56px; }
            .ct-heading-block { margin-bottom: 36px; }
            .ct-form { padding: 22px; }
            .ct-form__row { grid-template-columns: 1fr; }
            .ct-form__submit { width: 100%; justify-content: center; }
            .ct-map { height: 280px; }
            .ct-ticker__logo-item { padding: 10px 20px; }
            .ct-ticker__logo-img { height: 26px; max-width: 110px; }
          }

          a:focus-visible, button:focus-visible, input:focus-visible, textarea:focus-visible {
            outline: 2px solid #029add; outline-offset: 2px;
          }
          @media (prefers-reduced-motion: reduce) {
            .ct *, .ct *::before, .ct *::after { transition-duration: 0ms !important; }
            .ct-ticker__track { animation: none; }
          }
        `}</style>
      </div>
      <Footer />
    </>
  );
}

export default ContactPage;